// Animated module preview demos for the store catalog.

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, keyframes } from '@mui/material';
import { getAnimationTheme } from './storePanelTheme';
import { usePanelThemeMode } from './usePanelThemeMode';

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const drawLine = keyframes`
  from { stroke-dashoffset: 200; }
  to { stroke-dashoffset: 0; }
`;

const flow = keyframes`
  from { stroke-dashoffset: 24; }
  to { stroke-dashoffset: 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ring = keyframes`
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(8deg); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const dropPin = keyframes`
  from { transform: translateY(-40px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const scrollText = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
`;

function frameSx(t, embedded = false) {
  const base = {
    width: '100%',
    aspectRatio: '16 / 10',
    overflow: 'hidden',
    background: t.frameGradient,
    position: 'relative',
  };
  if (embedded) {
    return {
      ...base,
      borderRadius: 0,
      border: 'none',
      borderBottom: t.frameBorder,
    };
  }
  return {
    ...base,
    borderRadius: 2,
    border: t.frameBorder,
  };
}

function TrendsPreview({ t, embedded }) {
  return (
    <Box sx={frameSx(t, embedded)}>
      <svg viewBox="0 0 320 200" width="100%" height="100%">
        <defs>
          <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={t.chartFill} stopOpacity="0.35" />
            <stop offset="100%" stopColor={t.chartFill} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[40, 80, 120, 160].map((x) => (
          <line key={x} x1={x} y1="20" x2={x} y2="170" stroke={t.gridLine} strokeWidth="1" />
        ))}
        {[40, 80, 120, 160].map((y) => (
          <line key={y} x1="30" y1={y} x2="300" y2={y} stroke={t.gridLine} strokeWidth="1" />
        ))}
        <path
          d="M40 140 L80 110 L120 125 L160 70 L200 90 L240 45 L280 60"
          fill="none"
          stroke={t.chartLine}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="200"
          style={{ animation: `${drawLine} 2.5s ease-in-out infinite alternate` }}
        />
        <path
          d="M40 140 L80 110 L120 125 L160 70 L200 90 L240 45 L280 60 L280 170 L40 170 Z"
          fill="url(#tg)"
          opacity="0.6"
        />
        <circle cx="240" cy="45" r="5" fill={t.chartDot} style={{ animation: `${pulse} 1.2s ease infinite` }} />
      </svg>
    </Box>
  );
}

function TelegramPreview({ t, embedded }) {
  return (
    <Box sx={{ ...frameSx(t, embedded), display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box
        sx={{
          width: 120,
          height: 200,
          borderRadius: 3,
          border: `2px solid ${t.phoneBorder}`,
          bgcolor: t.phoneBg,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {[
          { delay: '0s', align: 'flex-start', color: t.bubbleIncoming },
          { delay: '0.4s', align: 'flex-end', color: t.bubbleOutgoing },
          { delay: '0.8s', align: 'flex-start', color: t.bubbleIncoming },
          { delay: '1.2s', align: 'flex-end', color: t.bubbleOutgoing },
        ].map((b, i) => (
          <Box
            key={i}
            sx={{
              alignSelf: b.align,
              maxWidth: '75%',
              px: 1,
              py: 0.75,
              borderRadius: 2,
              bgcolor: b.color,
              animation: `${slideUp} 0.5s ease ${b.delay} infinite alternate`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function TelegramAdvancedPreview({ t, embedded }) {
  const tokens = ['<alarm_name>', '<global.sum>', '<UID_A.tag_c[1]>', '<time>'];
  return (
    <Box sx={{ ...frameSx(t, embedded), display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, gap: 2 }}>
      <Box
        sx={{
          flex: 1,
          p: 1.5,
          borderRadius: 1,
          border: `1px solid ${t.phoneBorder}`,
          bgcolor: t.phoneBg,
          fontFamily: 'monospace',
          fontSize: 11,
          color: t.bubbleIncoming,
          minHeight: 120,
        }}
      >
        {tokens.map((tok, i) => (
          <Box
            key={tok}
            sx={{
              mb: 0.75,
              animation: `${blink} 1.2s ease ${i * 0.25}s infinite`,
              color: i % 2 ? t.bubbleOutgoing : t.bubbleIncoming,
            }}
          >
            {tok}
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          width: 100,
          height: 170,
          borderRadius: 3,
          border: `2px solid ${t.phoneBorder}`,
          bgcolor: t.phoneBg,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Box sx={{ px: 1, py: 0.75, borderRadius: 2, bgcolor: t.bubbleOutgoing, animation: `${slideUp} 0.6s ease infinite alternate` }}>
          <Box sx={{ fontSize: 9, color: t.cmdText, fontWeight: 700 }}>🚨 ALARM</Box>
          <Box sx={{ fontSize: 8, color: t.cmdMeta, mt: 0.5 }}>tag=12.4</Box>
        </Box>
        <Box sx={{ px: 1, py: 0.75, borderRadius: 2, bgcolor: t.bubbleIncoming, animation: `${slideUp} 0.8s ease 0.3s infinite alternate` }}>
          <Box sx={{ fontSize: 9, color: t.cmdText, fontWeight: 700 }}>✅ CLEARED</Box>
        </Box>
      </Box>
    </Box>
  );
}

function SvgHmiPreview({ t, embedded }) {
  return (
    <Box sx={frameSx(t, embedded)}>
      <svg viewBox="0 0 320 200" width="100%" height="100%">
        <rect x="20" y="60" width="80" height="100" rx="4" fill={t.hmiTankFill} stroke={t.hmiTankStroke} strokeWidth="2" />
        <rect x="120" y="80" width="80" height="60" rx="4" fill={t.hmiBoxFill} stroke={t.hmiBoxStroke} strokeWidth="2" />
        <rect x="220" y="60" width="80" height="100" rx="4" fill={t.hmiPumpFill} stroke={t.hmiPumpStroke} strokeWidth="2" />
        <path
          d="M100 110 L120 110 L120 110 L140 110"
          fill="none"
          stroke={t.hmiPipe}
          strokeWidth="4"
          strokeDasharray="8 4"
          style={{ animation: `${flow} 0.8s linear infinite` }}
        />
        <path
          d="M200 110 L220 110"
          fill="none"
          stroke={t.hmiPipe}
          strokeWidth="4"
          strokeDasharray="8 4"
          style={{ animation: `${flow} 0.8s linear infinite`, animationDelay: '0.2s' }}
        />
        <text x="45" y="115" fill={t.hmiLabelA} fontSize="11">Tank A</text>
        <text x="245" y="115" fill={t.hmiLabelB} fontSize="11">Pump</text>
        <text x="135" y="105" fill={t.hmiValue} fontSize="14" fontWeight="bold">72%</text>
      </svg>
    </Box>
  );
}

function ProjectTagsPreview({ t, embedded }) {
  return (
    <Box
      sx={{
        ...frameSx(t, embedded),
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        p: 2,
        alignContent: 'center',
        justifyContent: 'center',
      }}
    >
      {['flow_rate', 'temp_out', 'status_ok', 'pressure'].map((tag, i) => (
        <Box
          key={tag}
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: t.tagBg,
            border: t.tagBorder,
            color: t.tagText,
            fontSize: 12,
            fontFamily: 'monospace',
            animation: `${slideUp} 0.6s ease ${i * 0.15}s infinite alternate`,
          }}
        >
          {tag}
        </Box>
      ))}
    </Box>
  );
}

function AlarmRulesPreview({ t, embedded }) {
  return (
    <Box sx={{ ...frameSx(t, embedded), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ animation: `${ring} 0.5s ease-in-out infinite`, color: '#ff5252', fontSize: 64 }}>
        🔔
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          right: 16,
          p: 1,
          borderRadius: 1,
          bgcolor: t.alarmBannerBg,
          border: t.alarmBannerBorder,
          color: t.alarmBannerText,
          fontSize: 11,
          animation: `${blink} 1s step-end infinite`,
        }}
      >
        ALARM: pressure &gt; threshold
      </Box>
    </Box>
  );
}

function CommandsAdvancedPreview({ t, embedded }) {
  return (
    <Box sx={{ ...frameSx(t, embedded), display: 'flex', flexDirection: 'column', gap: 1, p: 2, justifyContent: 'center' }}>
      {['START pump', 'STOP pump', 'RESET fault'].map((label, i) => (
        <Box
          key={label}
          sx={{
            py: 1,
            px: 2,
            borderRadius: 1,
            bgcolor: i === 0 ? t.cmdActiveBg : t.cmdIdleBg,
            border: t.cmdBorder,
            color: t.cmdText,
            fontSize: 13,
            animation: `${pulse} 1.5s ease ${i * 0.3}s infinite`,
          }}
        >
          {label}
          <Box component="span" sx={{ float: 'right', color: t.cmdMeta, fontSize: 11 }}>
            P{i + 7}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MapsPreview({ t, embedded }) {
  return (
    <Box sx={frameSx(t, embedded)}>
      <svg viewBox="0 0 320 200" width="100%" height="100%">
        <rect width="320" height="200" fill={t.mapBg} />
        <path d="M0 100 Q80 60 160 100 T320 100" fill="none" stroke={t.mapRiverA} strokeWidth="2" opacity="0.55" />
        <path d="M0 130 Q100 90 200 130 T320 130" fill="none" stroke={t.mapRiverB} strokeWidth="1.5" opacity="0.45" />
        <path d="M40 40 L120 55 L200 35 L280 50" fill="none" stroke={t.gridLine} strokeWidth="1" opacity="0.35" />
        {[
          { x: 80, y: 70, delay: '0s' },
          { x: 160, y: 95, delay: '0.3s' },
          { x: 240, y: 75, delay: '0.6s' },
        ].map((p) => (
          <g key={`${p.x}-${p.y}`} style={{ animation: `${dropPin} 0.8s ease ${p.delay} infinite alternate` }}>
            <path d={`M${p.x} ${p.y} l-8 16 l16 0 z`} fill={t.mapPinHead} />
            <circle cx={p.x} cy={p.y - 4} r="6" fill={t.mapPin} />
          </g>
        ))}
      </svg>
    </Box>
  );
}

function DebugToolsPreview({ t, embedded }) {
  const lines = [
    '> GET /debug/packets/last',
    '{ "station": 3, "bytes": 128 }',
    '> POST /reload-config',
    'OK — config reloaded',
    '> unrecognized: 0 frames',
  ];
  return (
    <Box
      sx={{
        ...frameSx(t, embedded),
        p: 1.5,
        fontFamily: 'monospace',
        fontSize: 10,
        color: t.debugText,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ animation: `${scrollText} 4s linear infinite` }}>
        {[...lines, ...lines].map((line, i) => (
          <Box key={i} sx={{ py: 0.25, whiteSpace: 'nowrap' }}>
            {line}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function GenericPreview({ t, embedded }) {
  return (
    <Box
      sx={{
        ...frameSx(t, embedded),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: t.genericText,
        animation: `${pulse} 2s ease infinite`,
      }}
    >
      SCADA module
    </Box>
  );
}

const PREVIEWS = {
  trends: TrendsPreview,
  telegram: TelegramPreview,
  telegram_advanced: TelegramAdvancedPreview,
  svg_hmi: SvgHmiPreview,
  project_tags: ProjectTagsPreview,
  project_alarm_rules: AlarmRulesPreview,
  commands_advanced: CommandsAdvancedPreview,
  maps: MapsPreview,
  debug_tools: DebugToolsPreview,
};

TrendsPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
TelegramPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
TelegramAdvancedPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
SvgHmiPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
ProjectTagsPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
AlarmRulesPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
CommandsAdvancedPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
MapsPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
DebugToolsPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };
GenericPreview.propTypes = { t: PropTypes.object.isRequired, embedded: PropTypes.bool };

export default function ModulePreviewAnimation({
  animationKey,
  compact = false,
  mode: modeProp,
  embedded = false,
}) {
  const resolvedMode = usePanelThemeMode();
  const mode = modeProp || resolvedMode;
  const t = useMemo(() => getAnimationTheme(mode), [mode]);
  const Component = PREVIEWS[animationKey] || GenericPreview;

  return (
    <Box sx={compact ? { maxWidth: 280, mx: 'auto' } : undefined}>
      <Component t={t} embedded={embedded} />
    </Box>
  );
}

ModulePreviewAnimation.propTypes = {
  animationKey: PropTypes.string,
  compact: PropTypes.bool,
  mode: PropTypes.oneOf(['dark', 'light']),
  embedded: PropTypes.bool,
};
